import axios from 'axios'
import tokenService from './tokenService'

export function createApi(baseURL) {
  const api = axios.create({
    baseURL: baseURL,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  })

  // Interceptor per aggiungere il token a tutte le richieste
  api.interceptors.request.use(config => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Aggiungi il timestamp per evitare il caching
    config.params = {
      ...config.params,
      _t: Date.now()
    }
    return config
  })

  // Interceptor per gestire gli errori
  let isRefreshing = false;
  let failedQueue = [];
  let hasLoggedOut = false;
  
  const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    failedQueue = [];
  };

  /**api.interceptors.response.use(
    response => response,
    async error => {
      const originalRequest = error.config;
  
      // Se l'errore è 401 e non è una richiesta di refresh
      if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url.includes('/auth/refresh')) {
        if (isRefreshing) {
          // Se c'è già un refresh in corso, accoda la richiesta
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return api(originalRequest);
            })
            .catch(err => Promise.reject(err));
        }
  
        originalRequest._retry = true;
        isRefreshing = true;
  
        try {
          const { data } = await api.post('/auth/refresh');
          const { access_token } = data;
          
          localStorage.setItem('token', access_token);
          api.defaults.headers.common.Authorization = `Bearer ${access_token}`;
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
  
          processQueue(null, access_token);
          return api(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
  
      return Promise.reject(error);
    }
  )*/
api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (hasLoggedOut) {
      return new Promise(() => {});
    }

    // Se l'errore è 401
    if (error.response?.status === 401) {

      // Se è una richiesta di refresh fallita, forziamo logout
      if (originalRequest.url.includes('/auth/refresh')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // Se non abbiamo token o già abbiamo fatto retry → logout
      const token = localStorage.getItem('token');
      if (!token || originalRequest._retry) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // Gestione refresh token
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(newToken => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);
          })
          .catch(err => {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await api.post('/auth/refresh');
        const { access_token } = data;

        localStorage.setItem('token', access_token);
        api.defaults.headers.common.Authorization = `Bearer ${access_token}`;
        originalRequest.headers.Authorization = `Bearer ${access_token}`;

        processQueue(null, access_token);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        hasLoggedOut = true;
        return new Promise(() => {});
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

  return api;
}

export function createAuthService(api) {
  const authService = {
    async updateToken(data) {
      localStorage.setItem('token', data.access_token)
      tokenService.initializeAutoRefresh(data.expires_in)
    },
    async login(email, password) {
      const response = await api.post('/auth/login', { email, password })
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token)
        // Inizializza il refresh automatico
        tokenService.initializeAutoRefresh(response.data.expires_in)
        
        // Ottieni i dati utente dal token JWT tramite /auth/me
        try {
          const userResponse = await api.get('/auth/me')
          if (userResponse.data && userResponse.data.user) {
            localStorage.setItem('user', JSON.stringify(userResponse.data.user))
            return {
              ...response.data,
              user: userResponse.data.user
            }
          }
        } catch (error) {
          console.error('Error fetching user data:', error)
        }
      }
      return response.data
    },
  
    async logout() {
      try {
        await api.post('/auth/logout')
      } finally {
        localStorage.removeItem('token')
        localStorage.removeItem('user')
      }
    },
  
    async getUser() {
      const response = await api.get('/auth/me')
      return response.data
    },
  
    async forgotPassword(email) {
      const response = await api.post('/auth/forgot-password', { email })
      return response.data
    },
  
    async resetPassword(email, token, password, password_confirmation) {
      const response = await api.post('/auth/reset-password', {
        email,
        token,
        password,
        password_confirmation
      })
      return response.data
    }
  }
  return authService
}